"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { X } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import axiosInstance from "../../utils/axiosInstance";

const LEGACY_TYPES = [
  { value: "casual", label: "Casual" },
  { value: "sick", label: "Sick" },
  { value: "emergency", label: "Emergency" },
  { value: "other", label: "Other" },
];

const DURATIONS = [
  { value: "full-day", label: "Full Day" },
  { value: "half-day", label: "Half Day" },
  { value: "short-leave", label: "Short Leave" },
];

export function LeaveApplicationForm({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  useDynamicLeaveTypes = false,
  formError = "",
}) {
  const [leaveData, setLeaveData] = useState({
    startDate: "",
    endDate: "",
    reason: "",
    type: "casual",
    leaveType: "",
    duration: "full-day",
  });
  const [attachments, setAttachments] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [dynamicLeaveTypes, setDynamicLeaveTypes] = useState([]);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (useDynamicLeaveTypes && isOpen) {
      axiosInstance.get("/leave-types?activeOnly=true").then((res) => {
        if (res.data?.success && res.data?.data) setDynamicLeaveTypes(res.data.data);
      }).catch(() => setDynamicLeaveTypes([]));
    }
  }, [useDynamicLeaveTypes, isOpen]);

  useEffect(() => {
    if (initialData) {
      setLeaveData({
        startDate: new Date(initialData.startDate).toISOString().split("T")[0],
        endDate: new Date(initialData.endDate).toISOString().split("T")[0],
        reason: initialData.reason,
        type: initialData.type || "casual",
        leaveType: initialData.leaveType?._id || initialData.leaveType || "",
        duration: initialData.duration || "full-day",
      });
    } else {
      setLeaveData({
        startDate: "",
        endDate: "",
        reason: "",
        type: "casual",
        leaveType: "",
        duration: "full-day",
      });
    }
    setAttachments([]);
    setPreviewUrls([]);
    setFormErrors({});
  }, [initialData]);

  // Cleanup preview URLs when component unmounts
  useEffect(() => {
    return () => {
      previewUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLeaveData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleTypeChange = (value) => {
    setLeaveData((prev) => ({ ...prev, type: value }));
    setFormErrors((prev) => ({ ...prev, type: undefined }));
  };

  const handleLeaveTypeChange = (value) => {
    setLeaveData((prev) => ({ ...prev, leaveType: value }));
    setFormErrors((prev) => ({ ...prev, leaveType: undefined }));
  };

  const handleDurationChange = (value) => {
    setLeaveData((prev) => ({ ...prev, duration: value }));
    setFormErrors((prev) => ({ ...prev, duration: undefined }));
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setAttachments(files);
      
      // Create preview URLs for files
      const urls = files.map(file => URL.createObjectURL(file));
      setPreviewUrls(urls);
    }
  };

  const removeFile = (index) => {
    URL.revokeObjectURL(previewUrls[index]);
    setAttachments(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handlePreview = (url, fileName) => {
    window.open(url, '_blank', 'width=800,height=600');
  };

  const validateForm = () => {
    const errors = {};

    if (!leaveData.startDate) {
      errors.startDate = "Start date is required.";
    }
    if (!leaveData.endDate) {
      errors.endDate = "End date is required.";
    }
    if (leaveData.startDate && leaveData.endDate) {
      const start = new Date(leaveData.startDate);
      const end = new Date(leaveData.endDate);
      if (end < start) {
        errors.endDate = "End date cannot be before start date.";
      }
    }
    if (!leaveData.reason?.trim()) {
      errors.reason = "Reason is required.";
    }
    if (useDynamicLeaveTypes && dynamicLeaveTypes.length > 0 && !leaveData.leaveType) {
      errors.leaveType = "Leave type is required.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    onSubmit({ ...leaveData, attachments });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {initialData ? "Edit Leave Application" : "Apply for Leave"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {formError && (
            <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-md">
              {formError}
            </div>
          )}
          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
              Start Date <span className="text-red-500">*</span>
            </label>
            <Input
              type="date"
              id="startDate"
              name="startDate"
              value={leaveData.startDate}
              onChange={handleChange}
              required
              className={formErrors.startDate ? "border-red-500" : ""}
            />
            {formErrors.startDate && (
              <p className="text-sm text-red-500 mt-1">{formErrors.startDate}</p>
            )}
          </div>
          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">
              End Date <span className="text-red-500">*</span>
            </label>
            <Input
              type="date"
              id="endDate"
              name="endDate"
              value={leaveData.endDate}
              onChange={handleChange}
              required
              className={formErrors.endDate ? "border-red-500" : ""}
            />
            {formErrors.endDate && (
              <p className="text-sm text-red-500 mt-1">{formErrors.endDate}</p>
            )}
          </div>
          <div>
            <label htmlFor="reason" className="block text-sm font-medium text-gray-700">
              Reason <span className="text-red-500">*</span>
            </label>
            <Textarea
              id="reason"
              name="reason"
              value={leaveData.reason}
              onChange={handleChange}
              required
              className={formErrors.reason ? "border-red-500" : ""}
            />
            {formErrors.reason && (
              <p className="text-sm text-red-500 mt-1">{formErrors.reason}</p>
            )}
          </div>
          {useDynamicLeaveTypes && dynamicLeaveTypes.length > 0 ? (
            <>
              <div>
                <label htmlFor="leaveType" className="block text-sm font-medium text-gray-700">
                  Leave Type <span className="text-red-500">*</span>
                </label>
                <Select value={leaveData.leaveType} onValueChange={handleLeaveTypeChange}>
                  <SelectTrigger className={formErrors.leaveType ? "border-red-500" : ""}>
                    <SelectValue placeholder="Select leave type" />
                  </SelectTrigger>
                  <SelectContent>
                    {dynamicLeaveTypes.map((lt) => (
                      <SelectItem key={lt._id} value={lt._id}>
                        {lt.name} ({lt.code}) {lt.payType === "paid" ? "· Paid" : "· Unpaid"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formErrors.leaveType && (
                  <p className="text-sm text-red-500 mt-1">{formErrors.leaveType}</p>
                )}
              </div>
              <div>
                <label htmlFor="duration" className="block text-sm font-medium text-gray-700">
                  Duration <span className="text-red-500">*</span>
                </label>
                <Select value={leaveData.duration} onValueChange={handleDurationChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Duration" />
                  </SelectTrigger>
                  <SelectContent>
                    {DURATIONS.map((d) => (
                      <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          ) : (
            <div>
              <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                Leave Type <span className="text-red-500">*</span>
              </label>
              <Select value={leaveData.type} onValueChange={handleTypeChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select leave type" />
                </SelectTrigger>
                <SelectContent>
                  {LEGACY_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <label htmlFor="attachments" className="block text-sm font-medium text-gray-700">
              Attachments (optional)
            </label>
            <Input
              type="file"
              id="attachments"
              name="attachments"
              onChange={handleFileChange}
              multiple
              className="mb-2"
            />
            {attachments.length > 0 && (
              <div className="mt-2 space-y-2">
                {attachments.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                    <span className="text-sm truncate max-w-[200px]">{file.name}</span>
                    <div className="flex space-x-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handlePreview(previewUrls[index], file.name)}
                      >
                        Preview
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeFile(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <Button type="submit">Submit Leave Application</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}